import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { ErrorHandlerService } from '@core/errors/error-handler.service';
import { ModalType } from '@core/models/modal.types';
import { JexlService } from '@core/services/jexl.service';
import { ModalService } from '@core/services/modal.service';

import { ShippingAddressListModal } from '@features/program/modals/shipping-address-list/shipping-address-list.modal';
import { DeliveryAddressService } from '@features/program/services/delivery-address.service';

import { Button } from '@shared/components/button/button';
import type { ButtonConfig } from '@shared/components/button/button.model';
import { Feedback } from '@shared/components/feedback/feedback';
import type { FeedbackConfig } from '@shared/components/feedback/feedback.types';
import { TaskCard } from '@shared/components/task-card/task-card';
import type { TaskCardConfig } from '@shared/components/task-card/task-card.types';

import type { ProgramDeliveryDto } from '@swagger-services/program/model/programDeliveryDto';

import type { ProgramAddressTaskCardConfig } from './program-address-task-card.types';

@Component({
  selector: 'app-program-address-task-card',
  templateUrl: './program-address-task-card.html',
  imports: [Button, Feedback, TaskCard],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgramAddressTaskCard {
  private readonly modalService = inject(ModalService);
  private readonly deliveryAddressService = inject(DeliveryAddressService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly jexlService = inject(JexlService);

  readonly config = input.required<ProgramAddressTaskCardConfig>();

  protected readonly state = this.deliveryAddressService.state;
  protected readonly isLoading = computed(() => this.state().isLoading() || this.state().isInitial());
  protected readonly hasError = computed(() => this.state().isError());

  protected readonly errorFeedbackConfig = computed<FeedbackConfig>(() => {
    const error = this.state().getError();
    const errorContent = error ? this.errorHandlerService.getFeedbackConfig(error) : null;
    return {
      icon: 'alert-circle',
      iconColor: 'var(--p-red-500)',
      header: errorContent?.title ?? '',
      subhead: errorContent?.description,
    };
  });

  protected readonly errorButtonConfig = computed<ButtonConfig>(() => {
    const error = this.state().getError();
    const errorContent = error ? this.errorHandlerService.getFeedbackConfig(error) : null;
    return {
      label: errorContent?.buttonLabel,
      severity: 'secondary',
      rounded: true,
      fluid: true,
    };
  });

  protected readonly addresses = this.deliveryAddressService.addresses;
  protected readonly defaultAddress = this.deliveryAddressService.defaultAddress;

  protected readonly taskCardConfig = computed<TaskCardConfig>(() => {
    const address = this.defaultAddress();
    const config = this.config();

    if (!address) {
      return config.emptyState ?? config.taskCard;
    }

    return this.jexlService.process(config.taskCard, { address });
  });

  constructor() {
    afterNextRender(() => {
      const currentState = this.state();
      if (!currentState.isSuccess() && !currentState.isLoading()) {
        this.deliveryAddressService.loadAddresses(this.config().programId);
      }
    });
  }

  protected onRetry(): void {
    this.deliveryAddressService.loadAddresses(this.config().programId);
  }

  protected async onButtonClick(): Promise<void> {
    const programId = this.config().programId;
    const addresses = this.addresses();

    const result = await this.modalService.openModal(
      ShippingAddressListModal,
      { addresses, programId },
      ModalType.CARD,
    );

    const modalResult = result as { selectedAddress?: ProgramDeliveryDto } | undefined;
    if (modalResult?.selectedAddress) {
      this.deliveryAddressService.updateSelectedAddress(modalResult.selectedAddress);
    }
  }
}
